// Hardware back button → close topmost UI layer instead of exiting the app.
// Default behavior: scan known panels (innerHTML + React) and close the
// most-recently-opened one. Custom handlers can register via
// window._registerBackHandler(fn) — they run before default handlers.
//
// A handler returns true to indicate it handled the press and the chain stops.

const customStack = [];

export const registerBackHandler = (fn) => {
  if (typeof fn !== 'function') return () => {};
  customStack.push(fn);
  return () => {
    const idx = customStack.lastIndexOf(fn);
    if (idx >= 0) customStack.splice(idx, 1);
  };
};

const closeInnerHtmlPanel = (id) => {
  const el = document.getElementById(id);
  if (!el) return false;
  el.style.right = '-400px';
  setTimeout(() => el.remove(), 300);
  return true;
};

const popupOrToastClosed = () => {
  // Lightweight popups
  const popup = document.getElementById('notif-popup');
  if (popup) { popup.remove(); return true; }
  return false;
};

// Closers in order: most-recent / topmost first.
// Each returns true if it actually closed something.
const defaultClosers = [
  popupOrToastClosed,

  // React portal modals (highest z-index)
  () => {
    if (window._setFullScreenImage) {
      // If any full-screen image is open, the component listens for its own
      // close — call the setter with null to dismiss.
      const img = document.querySelector('[data-fullscreen-image="true"]');
      if (img) { window._setFullScreenImage?.(null); return true; }
    }
    return false;
  },
  () => {
    const modal = document.querySelector('[data-confirm-modal="true"]');
    if (modal && window._closeConfirmModal) { window._closeConfirmModal(); return true; }
    return false;
  },

  // innerHTML panels (mobile slide-in panels). Order = topmost first.
  () => closeInnerHtmlPanel('reactionsDetailsPanel'),
  () => closeInnerHtmlPanel('chatPanel'),
  () => closeInnerHtmlPanel('eventDetailsPanel'),
  () => closeInnerHtmlPanel('userProfilePanel'),
  () => closeInnerHtmlPanel('requestsPanel'),
  () => closeInnerHtmlPanel('eventsListPanel'),

  // React state-driven panels — match against existing selectors used by the app
  () => {
    if (!window._setNotificationsOpen) return false;
    const el = document.querySelector('[data-notifications-panel],[data-panel="notifications"]');
    if (!el) return false;
    window._setNotificationsOpen(false);
    return true;
  },
  () => {
    if (!window.setIsChatsListPanelOpen) return false;
    const el = document.getElementById('chatsListPanel');
    if (!el) return false;
    window.setIsChatsListPanelOpen(false);
    return true;
  },
  () => {
    if (!window.setIsProfilePanelOpen) return false;
    const el = document.getElementById('profilePanel');
    if (!el) return false;
    window.setIsProfilePanelOpen(false);
    return true;
  },

  // Tools menu (uses existing [data-tools-menu] selector)
  () => {
    if (!window._setIsToolsMenuOpen) return false;
    const el = document.querySelector('[data-tools-menu]');
    if (!el) return false;
    window._setIsToolsMenuOpen(false);
    return true;
  },
];

// Main entry point called from the Capacitor backButton listener.
export const handleNativeBack = () => {
  // Custom handlers run LIFO first
  for (let i = customStack.length - 1; i >= 0; i--) {
    try { if (customStack[i]() === true) return true; }
    catch (e) { console.error('Back handler error:', e); }
  }
  for (const closer of defaultClosers) {
    try { if (closer() === true) return true; }
    catch (e) { console.error('Back closer error:', e); }
  }
  return false;
};

window._registerBackHandler = registerBackHandler;
window._handleNativeBack = handleNativeBack;
